// React
import React, { useState } from 'react';

// Packages
import t from 'prop-types';
import AnimateHeight from 'react-animate-height';
import cc from 'classcat';

// Utilities
import { useLabels, useUser } from 'utilities/hooks';
import { ACCOUNTS } from 'utilities/configuration/permission';

// Components
import Video from 'components/video';

// Icons
import { FiPlayCircle, FiHelpCircle, FiX } from 'react-icons/fi';

const TutorialBarComponent = ({
    granteeTutorialKey,
    funderTutorialKey,
    theme,
}) => {
    // ///////////////////
    // HOOKS
    // ///////////////////

    const { label, tutorial } = useLabels();
    const { getUserAccountType } = useUser();

    // ///////////////////
    // STATE
    // ///////////////////

    const [isOpen, setIsOpen] = useState(false);
    const [showVideo, setShowVideo] = useState(false);

    // ///////////////////
    // METHODS
    // ///////////////////

    function toggle() {
        setIsOpen(!isOpen);
        // 300ms delay to remove video if closing
        if (isOpen) {
            setTimeout(() => {
                setShowVideo(false);
            }, 300);
        } else {
            setShowVideo(true);
        }
    }

    // ///////////////////
    // DATA
    // ///////////////////

    // Default is granteeTutorialKey
    const tutorialData =
        funderTutorialKey && getUserAccountType() === ACCOUNTS.funder
            ? tutorial(funderTutorialKey)
            : tutorial(granteeTutorialKey);

    const styles = {
        amber: {
            light: 'bg-amber-20 text-amber-300 border-amber-300',
            dark: 'bg-amber-300 text-white border-amber-300',
        },
        blue: {
            light: 'bg-blue-20 text-blue-300 border-blue-300',
            dark: 'bg-blue-300 text-white border-blue-300',
        },
        teal: {
            light: 'bg-teal-20 text-teal-300 border-teal-300',
            dark: 'bg-teal-300 text-white border-teal-300',
        },
        coral: {
            light: 'bg-coral-20 text-coral-300 border-coral-300',
            dark: 'bg-coral-300 text-white border-coral-300',
        },
    };

    // ///////////////////
    // RENDER
    // ///////////////////

    return tutorialData?.videoLink ? (
        <div className="flex my-24 select-none">
            <div
                onClick={() => toggle()}
                className={cc([
                    'flex gap-8 pt-8 pb-4 h-[42px] px-16 !font-bold cursor-pointer rounded-l-8 border-2 border-r-0 t-sh4 transition-all justify-center self-start',
                    styles[theme].dark,
                ])}>
                <FiPlayCircle />
                {!isOpen && <span>{label('TutorialBarTitle')}</span>}
            </div>
            <div
                title={`${label('TutorialBarTitle')}: ${tutorialData?.title}`}
                className={cc(['flex flex-grow shadow-sm cursor-pointer'])}
                onClick={() => toggle()}>
                <div
                    className={cc([
                        'flex flex-col flex-grow rounded-r-8 px-16 pt-8 pb-4 transition-all border-2 t-sh4 ',
                        styles[theme].light,
                        { 'rounded-bl-8': isOpen },
                    ])}>
                    <div className="flex justify-between">
                        <span>{tutorialData?.title}</span>
                        {isOpen ? <FiX /> : <FiHelpCircle />}
                    </div>
                    <AnimateHeight
                        duration={300}
                        height={isOpen ? 'auto' : 0}
                        className="relative -top-2 rounded-b-8">
                        <div
                            onClick={event => {
                                event.stopPropagation();
                            }}
                            className="mt-8 mb-12">
                            {showVideo ? (
                                <Video
                                    id={tutorialData?.videoLink}
                                    title={tutorialData?.title}
                                />
                            ) : null}
                        </div>
                    </AnimateHeight>
                </div>
            </div>
        </div>
    ) : null;
};

TutorialBarComponent.propTypes = {
    tutorialKey: t.string.isRequired,
    theme: t.oneOf(['blue', 'teal', 'coral', 'amber']),
};

TutorialBarComponent.defaultProps = {
    tutorialKey: '',
    theme: 'amber',
};

export default TutorialBarComponent;
